import React from "react";

import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";
import { App } from "./app";
import { Chat } from "./chat/chat";
import { DataLogs } from "./datalogs/datalog";
import { Manager } from "./manager/manager";
import { PlanningOps } from "./manager/operations/planningOps";
import { DataLoggingDashboard } from "./monitoring/dataLoggingDashboard";
import { DroneSettings } from "./settings/droneSettings";
import { HMSSettings } from "./settings/hmsSettings";
import { CreateUser } from "./users/createUser";
import { Forgot } from "./users/forgotPassword";
import { Login } from "./users/login";
import { Reset } from "./users/resetPassword";
import { UserInfo } from "./users/userInfo";
import { UserList } from "./users/userList";
import { UserNotification } from "./users/userNotifications";
import { ZCASettings } from "./settings/zcaSettings";
import { InfrastructureDashboard } from "./manager/infrastructure/infrastructureDashboard";

import { CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { UserAuthContextProvider } from "./contexts/authContext";
import { EnvProvider } from "./contexts/envContext";
import { MapProvider } from "./contexts/mapContext";
import { ChatProvider } from "./contexts/chatContext";
import { CuasProvider } from "./contexts/cuasContext";

import ErrorBoundary from "./errorBoundary";
import OperatorDashboard from "./manager/operations/operatorDashboard";
import OpManagerDashboard from "./manager/operations/opManagerDashboard";
import Settings from "./settings/settings";
import AssetMap from "./cuas/assetMap";
import MapComponent from "./map/mapComponent";
import AlertCreator from "./manager/alerts/alertCreator";
import ConstraintCreator from "./manager/constraints/constraintCreator";
import COACreator from "./manager/coa/coaCreator";
import MapPlaybackDatagrid from "./mapPlayback/mapPlaybackDatagrid";
import AnalysisDashboard from "./analysis/analysisDashboard";
import HimsDashboard from "./hms/hmsDashboard";
import Forbidden from "./forbidden";
import OrganizationList from "./organizations/orgList";
import DfrApp from "./dfr/dfrApp";
import FeedbackSubmissionsDatagrid from "./feedback/feedbackSubmissionsDatagrid";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import "./css/cmp.css";

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        text: {
            secondary: "#6b778c"
        }
    }
});

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
    <ThemeProvider theme={darkTheme}>
        <CssBaseline />

        <BrowserRouter>
            <ErrorBoundary>
                <EnvProvider>
                    <UserAuthContextProvider>
                        <MapProvider>
                            <ChatProvider>
                                <CuasProvider>
                                    <Routes>
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/signup" element={<CreateUser />} />
                                        <Route path="/reset" element={<Reset />} />
                                        <Route path="/forgot" element={<Forgot />} />
                                        <Route path="/dfr" element={<DfrApp />} />

                                        <Route path="/" element={<App />}>
                                            <Route path="" element={<Manager />} />
                                            <Route path="chat" element={<Chat />} />
                                            <Route path="manage" element={<UserList />} />
                                            <Route path="organizations" element={<OrganizationList />} />
                                            <Route path="account" element={<UserInfo />} />
                                            <Route path="datalogs" element={<DataLogs />} />
                                            <Route path="hms" element={<HimsDashboard />} />
                                            <Route path="operations" element={<PlanningOps />} />
                                            <Route path="constraints" element={<ConstraintCreator />} />
                                            <Route path="alerts" element={<AlertCreator />} />
                                            <Route path="coa" element={<COACreator />} />
                                            <Route path="map-settings" element={<Settings />} />
                                            <Route path="notifications" element={<UserNotification />} />
                                            <Route path="monitoring" element={<DataLoggingDashboard />} />
                                            <Route path="map" element={<MapComponent standalone={true} />} />
                                            <Route path="drone-settings" element={<DroneSettings />} />
                                            <Route path="hms-settings" element={<HMSSettings />} />
                                            <Route path="zca-settings" element={<ZCASettings />} />
                                            <Route path="dashboard" element={<OperatorDashboard />} />
                                            <Route path="infrastructure" element={<InfrastructureDashboard />} />
                                            <Route path="op-manager-dashboard" element={<OpManagerDashboard />} />
                                            <Route path="analysis" element={<AnalysisDashboard />} />
                                            <Route path="asset-map" element={<AssetMap />} />
                                            <Route path="forbidden" element={<Forbidden />} />
                                            <Route path="playback" element={<MapPlaybackDatagrid />} />
                                            <Route path="feedback" element={<FeedbackSubmissionsDatagrid />} />
                                        </Route>
                                    </Routes>
                                </CuasProvider>
                            </ChatProvider>
                        </MapProvider>
                    </UserAuthContextProvider>
                </EnvProvider>
            </ErrorBoundary>
        </BrowserRouter>
    </ThemeProvider>
);
